import { FC } from 'react';

import { withA11yVisuallyHidden } from '@telialt/common-a11y/src/withA11yVisuallyHidden';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { formatCurrency, withParams } from '@telialt/utils/src/helpers';

import { ORDER_CART_ENTRY_ID, ORDER_CART_TEST_ID } from '../constants';
import { Price } from '../model';
import {
    CellIcon,
    CellPrice,
    CellTitle,
    FuturePriceRow,
    FuturePriceRowContent,
    TvPlanCommitmentText,
    TvPlanEntryListItem,
} from './entries.styles';

export type TvPlanEntryProps = {
    name: string;
    monthlyPrice: Price;
    futurePrice?: Price;
    futurePriceDate?: string;
    commitmentText?: string;
};

const FuturePriceTextAccessible = withA11yVisuallyHidden(FuturePriceRowContent);

export const TvPlanEntry: FC<TvPlanEntryProps> = ({
    name,
    monthlyPrice,
    futurePrice,
    futurePriceDate,
    commitmentText,
}) => {
    const { t } = useTranslations();

    const futurePriceText =
        futurePrice && futurePriceDate
            ? withParams(t['order_cart.entry_tv_plan.future_price'], {
                  futurePriceDate,
                  futurePrice: futurePrice.formattedValue,
              })
            : null;
    const futurePriceA11y = futurePrice
        ? withParams(t['order_cart.monthly_price_unit_accessible'], {
              amount: formatCurrency(futurePrice.value),
          })
        : null;

    const futurePriceTextA11y =
        futurePriceText && futurePriceA11y
            ? withParams(t['order_cart.entry_tv_plan.future_price'], {
                  futurePriceDate,
                  futurePrice: futurePriceA11y,
              })
            : null;

    return (
        <TvPlanEntryListItem data-test={ORDER_CART_ENTRY_ID.TV_PLAN} aria-label={name}>
            <CellIcon icon="doc" />
            <CellTitle>{name}</CellTitle>
            <CellPrice price={monthlyPrice} isMonthly={true} />

            <FuturePriceRow data-test={ORDER_CART_TEST_ID.FUTURE_PRICE}>
                {futurePriceText && (
                    <FuturePriceTextAccessible accessible={futurePriceTextA11y}>
                        {futurePriceText}
                    </FuturePriceTextAccessible>
                )}
            </FuturePriceRow>
            <TvPlanCommitmentText>{commitmentText}</TvPlanCommitmentText>
        </TvPlanEntryListItem>
    );
};
